/**
 * Rank tier helpers for club leaderboards.
 * Preview mode highlights podium (top 3); full mode adds top 10 and top 25 bands.
 *
 * Styling uses semantic tokens (warning, team2, primary, etc.) so light/dark
 * modes adapt via CSS variables — no raw slate/amber pairs.
 */

/** @typedef {'gold' | 'silver' | 'bronze' | 'top10' | 'top25'} RankTier */

/**
 * @param {number} rank
 * @param {'none' | 'preview' | 'full'} mode
 * @returns {RankTier | null}
 */
export function getRankTier(rank, mode = 'full') {
  if (!rank || rank < 1 || mode === 'none') return null;
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  if (mode === 'preview') {
    if (rank <= 10) return 'top10';
    return null;
  }
  if (rank <= 10) return 'top10';
  if (rank <= 25) return 'top25';
  return null;
}

export const RANK_TIER_CLASSES = {
  gold: {
    card: 'bg-gradient-to-r from-warning-light to-surface-subtle border border-warning/25 shadow-brand-sm',
    rank: 'border-warning/40 bg-gradient-to-br from-warning to-warning-dark text-text-inverse shadow-sm',
  },
  silver: {
    card: 'bg-gradient-to-r from-surface-elevated via-surface-subtle to-surface border border-border-medium shadow-brand-sm',
    rank: 'border-border-medium bg-gradient-to-br from-text-secondary to-text-muted text-text-inverse shadow-sm',
  },
  bronze: {
    card: 'bg-gradient-to-r from-team2-light to-surface-subtle border border-team2/25 shadow-brand-sm',
    rank: 'border-team2/50 bg-gradient-to-br from-team2 to-team2-border text-text-inverse shadow-sm',
  },
  top10: {
    card: 'bg-primary-light border border-primary/25',
    rank: 'border-primary/30 bg-primary-light text-primary',
  },
  top25: {
    card: 'bg-info-light border border-info/25',
    rank: 'border-info/30 bg-info-light text-info-dark',
  },
};

/** Table row backgrounds — lighter than pill cards so dense tables stay readable. */
export const RANK_TIER_TABLE_ROW = {
  gold: 'bg-gradient-to-r from-warning-light/80 to-transparent',
  silver: 'bg-gradient-to-r from-surface-elevated/60 to-transparent',
  bronze: 'bg-gradient-to-r from-team2-light/80 to-transparent',
  top10: 'bg-primary-light/50',
  top25: 'bg-info-light/50',
};

const USER_HIGHLIGHT_ROW =
  'bg-primary/10 [&>td:first-child]:border-l-4 [&>td:first-child]:border-primary [&>td:first-child]:pl-2 ring-1 ring-inset ring-primary/25';

/**
 * @param {number} rank
 * @param {'none' | 'preview' | 'full'} mode
 * @param {{ isUserHighlight?: boolean }} [options]
 * @returns {string}
 */
export function getRankTierTableRowClass(rank, mode = 'full', { isUserHighlight = false } = {}) {
  if (isUserHighlight) return USER_HIGHLIGHT_ROW;
  const tier = getRankTier(rank, mode);
  if (!tier) return '';
  return RANK_TIER_TABLE_ROW[tier] || '';
}

/**
 * @param {number} rank
 * @param {'none' | 'preview' | 'full'} mode
 * @returns {string}
 */
export function getRankTierBadgeClass(rank, mode = 'full') {
  const tier = getRankTier(rank, mode);
  if (!tier) return '';
  return RANK_TIER_CLASSES[tier]?.rank || '';
}
